import { useState, useEffect } from 'react';
import { propertiesAPI, channexAPI, usersAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { ImageUploader } from '../../components/ImageUploader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Building2,
  Loader2,
  RefreshCw,
  MapPin,
  Mail,
  Wifi,
  AlertCircle,
  CreditCard,
  ToggleLeft,
  ToggleRight,
  Bell,
  BellRing,
  Download,
  ScrollText,
  Zap,
  Globe,
  Image as ImageIcon,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { CURRENCIES as ALL_CURRENCIES } from '../../lib/currencies';

const initialFormData = {
  name: '',
  slug: '',
  currency: 'USD',
  timezone: 'Europe/London',
  address: '',
  city: '',
  country: '',
  contact_email: '',
  description: '',
  image_url: '',
  gallery: [],
  channex_api_key: '',
  channex_property_id: '',
  mollie_api_key: '',
  payment_mode: 'pay_at_property',
  mollie_profile_id: '',
};

export default function AdminProperties() {
  const { user: currentUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState('details');
  const [webhookLogsOpen, setWebhookLogsOpen] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookLogsLoading, setWebhookLogsLoading] = useState(false);
  const [webhookLogsProperty, setWebhookLogsProperty] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [propertyUserIds, setPropertyUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSaving, setUsersSaving] = useState(false);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  const fetchProperties = async () => {
    try {
      const response = await propertiesAPI.list();
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedProperty) {
        await propertiesAPI.update(selectedProperty.id, formData);
        toast.success('Property updated successfully');
      } else {
        await propertiesAPI.create(formData);
        toast.success('Property created successfully');
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      slug: property.slug || '',
      currency: property.currency || 'USD',
      timezone: property.timezone || 'Europe/London',
      address: property.address,
      city: property.city,
      country: property.country,
      contact_email: property.contact_email,
      description: property.description || '',
      image_url: property.image_url || '',
      gallery: property.gallery || [],
      channex_api_key: '',
      channex_property_id: property.channex_property_id || '',
      mollie_api_key: '',
      payment_mode: property.payment_mode || 'pay_at_property',
      mollie_profile_id: property.mollie_profile_id || '',
    });
    setDialogOpen(true);
    setFormTab('details');
  };

  const handleDelete = async () => {
    if (!selectedProperty) return;

    try {
      await propertiesAPI.delete(selectedProperty.id);
      toast.success('Property deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleSyncChannex = async (property) => {
    try {
      const result = await propertiesAPI.syncChannex(property.id);
      toast.success('Channex sync completed successfully', {
        description: `Found ${result.data?.room_types?.data?.data?.length || 0} room types, ${result.data?.rate_plans?.data?.data?.length || 0} rate plans`
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Channex sync failed');
    }
  };

  const handleTestChannex = async (property) => {
    try {
      const result = await propertiesAPI.testChannex(property.id);
      toast.success('Channex connection successful!', {
        description: `Connected to: ${result.data?.property_name || 'Property'}`
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Connection test failed';
      toast.error('Channex connection failed', {
        description: detail.includes('Booking CRS App') 
          ? 'Please install the Booking CRS App in Channex dashboard'
          : detail
      });
    }
  };

  const handleRegisterWebhook = async (property) => {
    setActionLoading(prev => ({ ...prev, [`webhook-${property.id}`]: true }));
    try {
      const result = await channexAPI.registerWebhook(property.id);
      toast.success('Webhook registered!', {
        description: `Channex will now push booking updates to your app in real-time.`
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to register webhook';
      toast.error('Webhook registration failed', { description: detail });
    } finally {
      setActionLoading(prev => ({ ...prev, [`webhook-${property.id}`]: false }));
    }
  };

  const handleSyncBookings = async (property) => {
    setActionLoading(prev => ({ ...prev, [`sync-${property.id}`]: true }));
    try {
      const result = await channexAPI.syncBookings(property.id);
      const data = result.data;
      if (data.total_revisions === 0) {
        toast.info('No new bookings to sync', {
          description: 'All booking revisions are already up to date.'
        });
      } else {
        const created = data.results?.filter(r => r.action === 'created').length || 0;
        const updated = data.results?.filter(r => r.action === 'updated').length || 0;
        const cancelled = data.results?.filter(r => r.action === 'cancelled').length || 0;
        toast.success(`Synced ${data.processed} booking(s)`, {
          description: [
            created && `${created} new`,
            updated && `${updated} updated`,
            cancelled && `${cancelled} cancelled`
          ].filter(Boolean).join(', ')
        });
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Sync failed';
      toast.error('Booking sync failed', { description: detail });
    } finally {
      setActionLoading(prev => ({ ...prev, [`sync-${property.id}`]: false }));
    }
  };

  const handleViewWebhookLogs = async (property) => {
    setWebhookLogsProperty(property);
    setWebhookLogsOpen(true);
    setWebhookLogsLoading(true);
    try {
      const result = await channexAPI.getWebhookLogs(property.id, 50);
      setWebhookLogs(result.data.logs || []);
    } catch (error) {
      toast.error('Failed to load webhook logs');
      setWebhookLogs([]);
    } finally {
      setWebhookLogsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedProperty(null);
    setFormData(initialFormData);
    setFormTab('details');
    setAllUsers([]);
    setPropertyUserIds([]);
    setDialogOpen(true);
  };

  const loadPropertyUsers = async (propertyId) => {
    setUsersLoading(true);
    try {
      const [usersRes, propUsersRes] = await Promise.all([
        usersAPI.list(),
        propertiesAPI.getUsers(propertyId),
      ]);
      setAllUsers(usersRes.data || []);
      setPropertyUserIds((propUsersRes.data || []).map(u => u.id));
    } catch {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSaveUsers = async () => {
    if (!selectedProperty) return;
    setUsersSaving(true);
    try {
      await propertiesAPI.updateUsers(selectedProperty.id, propertyUserIds);
      toast.success('User assignments updated');
    } catch {
      toast.error('Failed to update user assignments');
    } finally {
      setUsersSaving(false);
    }
  };

  const toggleUserAssignment = (userId) => {
    setPropertyUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-wrapper" data-testid="properties-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Properties</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your hotel properties and Channex integration.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="add-property-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0">
              <DialogHeader className="px-5 pt-5 pb-3 border-b">
                <DialogTitle className="text-lg font-semibold">
                  {selectedProperty ? 'Edit Property' : 'Add New Property'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col" style={{ height: 'calc(90vh - 130px)' }}>
                <div className="flex flex-1 overflow-hidden">
                  {/* Tab sidebar */}
                  <div className="w-[180px] border-r bg-gray-50/50 overflow-y-auto flex-shrink-0">
                    {[
                      { id: 'details', label: 'Details', icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50' },
                      { id: 'location', label: 'Location', icon: MapPin, color: 'text-green-600', bg: 'bg-green-50' },
                      { id: 'gallery', label: 'Gallery', icon: ImageIcon, color: 'text-purple-600', bg: 'bg-purple-50' },
                      { id: 'channex', label: 'Channex', icon: Wifi, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                      { id: 'payments', label: 'Payments', icon: CreditCard, color: 'text-amber-600', bg: 'bg-amber-50' },
                      ...(selectedProperty ? [{ id: 'users', label: 'Users', icon: UserPlus, color: 'text-indigo-600', bg: 'bg-indigo-50' }] : []),
                    ].map(tab => {
                      const Icon = tab.icon;
                      const active = formTab === tab.id;
                      return (
                        <button key={tab.id} type="button" onClick={() => { setFormTab(tab.id); if (tab.id === 'users' && selectedProperty) loadPropertyUsers(selectedProperty.id); }}
                          className={`w-full flex items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors ${active ? 'bg-white border-r-2 border-blue-600 font-semibold text-gray-900' : 'text-gray-600 hover:bg-gray-100'}`}
                          data-testid={`prop-tab-${tab.id}`}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? tab.bg : 'bg-transparent'}`}>
                            <Icon className={`h-4 w-4 ${active ? tab.color : 'text-gray-400'}`} />
                          </div>
                          <span className="truncate">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Panel content */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Details tab */}
                    {formTab === 'details' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Property Name *</Label>
                            <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Grand Hotel" required data-testid="property-name-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact_email">Contact Email *</Label>
                            <Input id="contact_email" name="contact_email" type="email" value={formData.contact_email} onChange={handleInputChange} placeholder="contact@hotel.com" required data-testid="property-email-input" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="slug">URL Slug *</Label>
                            <Input id="slug" name="slug" value={formData.slug} onChange={handleInputChange} placeholder="grand-hotel" required data-testid="property-slug-input" />
                            <p className="text-xs text-muted-foreground">Public URL: /{formData.slug || 'slug'}</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="currency">Currency *</Label>
                            <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="property-currency-select">
                              {ALL_CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>))}
                            </select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="timezone">Timezone *</Label>
                          <select id="timezone" name="timezone" value={formData.timezone} onChange={handleInputChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="property-timezone-select">
                            {[
                              { tz: 'Pacific/Pago_Pago', label: 'Pacific/Pago Pago (Pago Pago, Samoa)' },
                              { tz: 'Pacific/Honolulu', label: 'Pacific/Honolulu (Hawaii)' },
                              { tz: 'America/Anchorage', label: 'America/Anchorage (Alaska)' },
                              { tz: 'America/Los_Angeles', label: 'America/Los Angeles (US Pacific)' },
                              { tz: 'America/Denver', label: 'America/Denver (US Mountain)' },
                              { tz: 'America/Chicago', label: 'America/Chicago (US Central)' },
                              { tz: 'America/Mexico_City', label: 'America/Mexico City (Mexico City)' },
                              { tz: 'America/New_York', label: 'America/New York (US Eastern)' },
                              { tz: 'America/Toronto', label: 'America/Toronto (Ottawa, Canada)' },
                              { tz: 'America/Bogota', label: 'America/Bogota (Bogotá, Colombia)' },
                              { tz: 'America/Lima', label: 'America/Lima (Lima, Peru)' },
                              { tz: 'America/Santiago', label: 'America/Santiago (Santiago, Chile)' },
                              { tz: 'America/Argentina/Buenos_Aires', label: 'America/Buenos Aires (Argentina)' },
                              { tz: 'America/Sao_Paulo', label: 'America/São Paulo (Brasília, Brazil)' },
                              { tz: 'Atlantic/Reykjavik', label: 'Atlantic/Reykjavik (Reykjavik, Iceland)' },
                              { tz: 'Europe/London', label: 'Europe/London (London, UK)' },
                              { tz: 'Europe/Dublin', label: 'Europe/Dublin (Dublin, Ireland)' },
                              { tz: 'Europe/Lisbon', label: 'Europe/Lisbon (Lisbon, Portugal)' },
                              { tz: 'Europe/Paris', label: 'Europe/Paris (Paris, France)' },
                              { tz: 'Europe/Berlin', label: 'Europe/Berlin (Berlin, Germany)' },
                              { tz: 'Europe/Amsterdam', label: 'Europe/Amsterdam (Amsterdam, Netherlands)' },
                              { tz: 'Europe/Brussels', label: 'Europe/Brussels (Brussels, Belgium)' },
                              { tz: 'Europe/Madrid', label: 'Europe/Madrid (Madrid, Spain)' },
                              { tz: 'Europe/Rome', label: 'Europe/Rome (Rome, Italy)' },
                              { tz: 'Europe/Zurich', label: 'Europe/Zurich (Bern, Switzerland)' },
                              { tz: 'Europe/Vienna', label: 'Europe/Vienna (Vienna, Austria)' },
                              { tz: 'Europe/Prague', label: 'Europe/Prague (Prague, Czech Republic)' },
                              { tz: 'Europe/Warsaw', label: 'Europe/Warsaw (Warsaw, Poland)' },
                              { tz: 'Europe/Stockholm', label: 'Europe/Stockholm (Stockholm, Sweden)' },
                              { tz: 'Europe/Oslo', label: 'Europe/Oslo (Oslo, Norway)' },
                              { tz: 'Europe/Copenhagen', label: 'Europe/Copenhagen (Copenhagen, Denmark)' },
                              { tz: 'Europe/Helsinki', label: 'Europe/Helsinki (Helsinki, Finland)' },
                              { tz: 'Europe/Athens', label: 'Europe/Athens (Athens, Greece)' },
                              { tz: 'Europe/Bucharest', label: 'Europe/Bucharest (Bucharest, Romania)' },
                              { tz: 'Europe/Istanbul', label: 'Europe/Istanbul (Ankara, Turkey)' },
                              { tz: 'Europe/Moscow', label: 'Europe/Moscow (Moscow, Russia)' },
                              { tz: 'Europe/Kyiv', label: 'Europe/Kyiv (Kyiv, Ukraine)' },
                              { tz: 'Africa/Cairo', label: 'Africa/Cairo (Cairo, Egypt)' },
                              { tz: 'Africa/Johannesburg', label: 'Africa/Johannesburg (Pretoria, South Africa)' },
                              { tz: 'Africa/Lagos', label: 'Africa/Lagos (Abuja, Nigeria)' },
                              { tz: 'Africa/Nairobi', label: 'Africa/Nairobi (Nairobi, Kenya)' },
                              { tz: 'Africa/Casablanca', label: 'Africa/Casablanca (Rabat, Morocco)' },
                              { tz: 'Africa/Addis_Ababa', label: 'Africa/Addis Ababa (Addis Ababa, Ethiopia)' },
                              { tz: 'Asia/Jerusalem', label: 'Asia/Jerusalem (Jerusalem, Israel)' },
                              { tz: 'Asia/Beirut', label: 'Asia/Beirut (Beirut, Lebanon)' },
                              { tz: 'Asia/Riyadh', label: 'Asia/Riyadh (Riyadh, Saudi Arabia)' },
                              { tz: 'Asia/Dubai', label: 'Asia/Dubai (Abu Dhabi, UAE)' },
                              { tz: 'Asia/Tehran', label: 'Asia/Tehran (Tehran, Iran)' },
                              { tz: 'Asia/Karachi', label: 'Asia/Karachi (Islamabad, Pakistan)' },
                              { tz: 'Asia/Kolkata', label: 'Asia/Kolkata (New Delhi, India)' },
                              { tz: 'Asia/Colombo', label: 'Asia/Colombo (Sri Lanka)' },
                              { tz: 'Asia/Kathmandu', label: 'Asia/Kathmandu (Kathmandu, Nepal)' },
                              { tz: 'Asia/Dhaka', label: 'Asia/Dhaka (Dhaka, Bangladesh)' },
                              { tz: 'Asia/Bangkok', label: 'Asia/Bangkok (Bangkok, Thailand)' },
                              { tz: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho Chi Minh (Hanoi, Vietnam)' },
                              { tz: 'Asia/Jakarta', label: 'Asia/Jakarta (Jakarta, Indonesia)' },
                              { tz: 'Asia/Singapore', label: 'Asia/Singapore (Singapore)' },
                              { tz: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala Lumpur (Malaysia)' },
                              { tz: 'Asia/Manila', label: 'Asia/Manila (Manila, Philippines)' },
                              { tz: 'Asia/Shanghai', label: 'Asia/Shanghai (Beijing, China)' },
                              { tz: 'Asia/Hong_Kong', label: 'Asia/Hong Kong (Hong Kong)' },
                              { tz: 'Asia/Taipei', label: 'Asia/Taipei (Taipei, Taiwan)' },
                              { tz: 'Asia/Seoul', label: 'Asia/Seoul (Seoul, South Korea)' },
                              { tz: 'Asia/Tokyo', label: 'Asia/Tokyo (Tokyo, Japan)' },
                              { tz: 'Australia/Perth', label: 'Australia/Perth (Perth)' },
                              { tz: 'Australia/Sydney', label: 'Australia/Sydney (Canberra, Australia)' },
                              { tz: 'Australia/Melbourne', label: 'Australia/Melbourne (Melbourne)' },
                              { tz: 'Pacific/Auckland', label: 'Pacific/Auckland (Wellington, New Zealand)' },
                              { tz: 'Pacific/Fiji', label: 'Pacific/Fiji (Suva, Fiji)' },
                            ].map((item) => (<option key={item.tz} value={item.tz}>{item.label}</option>))}
                          </select>
                        </div>
                      </>
                    )}

                    {/* Location tab */}
                    {formTab === 'location' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="address">Address *</Label>
                          <Input id="address" name="address" value={formData.address} onChange={handleInputChange} placeholder="123 Main Street" required data-testid="property-address-input" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="city">City *</Label>
                            <Input id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="Amsterdam" required data-testid="property-city-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="country">Country *</Label>
                            <Input id="country" name="country" value={formData.country} onChange={handleInputChange} placeholder="Netherlands" required data-testid="property-country-input" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="description">Description</Label>
                          <Textarea id="description" name="description" value={formData.description} onChange={handleInputChange} placeholder="A beautiful hotel in the heart of the city..." rows={4} data-testid="property-description-input" />
                        </div>
                      </>
                    )}

                    {/* Gallery tab */}
                    {formTab === 'gallery' && (
                      <>
                        <div className="space-y-2">
                          <Label>Property Gallery</Label>
                          <ImageUploader images={formData.gallery} onChange={(imgs) => setFormData((prev) => ({ ...prev, gallery: imgs }))} maxImages={15} />
                          <p className="text-xs text-muted-foreground">First image is used as the cover photo. Drag to reorder.</p>
                        </div>
                      </>
                    )}

                    {/* Channex tab */}
                    {formTab === 'channex' && (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <Wifi className="h-5 w-5 text-emerald-500" />
                          <h3 className="font-medium text-gray-900">Channex Integration</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">Connect your property to Channex for OTA channel management and booking synchronisation.</p>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="channex_api_key">Channex API Key</Label>
                            <Input id="channex_api_key" name="channex_api_key" type="password" value={formData.channex_api_key} onChange={handleInputChange} placeholder="Enter API key" data-testid="channex-api-key-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="channex_property_id">Channex Property ID</Label>
                            <Input id="channex_property_id" name="channex_property_id" value={formData.channex_property_id} onChange={handleInputChange} placeholder="Enter Property ID" data-testid="channex-property-id-input" />
                          </div>
                        </div>
                        {selectedProperty?.channex_property_id && (
                          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <p className="text-xs font-medium text-emerald-700">Connected to Channex</p>
                            <p className="text-xs text-emerald-600 mt-0.5">Property ID: {selectedProperty.channex_property_id}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Payments tab */}
                    {formTab === 'payments' && (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-5 w-5 text-amber-500" />
                          <h3 className="font-medium text-gray-900">Payment Configuration</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">Configure Mollie payment gateway for online payments.</p>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="mollie_api_key">Mollie API Key {selectedProperty?.mollie_configured && <Badge variant="outline" className="ml-1 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Configured</Badge>}</Label>
                            <Input id="mollie_api_key" name="mollie_api_key" type="password" value={formData.mollie_api_key} onChange={handleInputChange} placeholder={selectedProperty?.mollie_configured ? '(unchanged - leave blank to keep)' : 'test_xxxx or live_xxxx'} data-testid="mollie-key-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="mollie_profile_id">Mollie Profile ID</Label>
                            <Input id="mollie_profile_id" name="mollie_profile_id" value={formData.mollie_profile_id} onChange={handleInputChange} placeholder="pfl_xxxxxxxxxx" data-testid="mollie-profile-input" />
                            <p className="text-xs text-muted-foreground">Found in Mollie Dashboard &rarr; Settings &rarr; Website profiles</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="payment_mode">Payment Mode</Label>
                            <select id="payment_mode" name="payment_mode" value={formData.payment_mode} onChange={handleInputChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="payment-mode-select">
                              <option value="pay_at_property">Pay at Property Only</option>
                              <option value="online">Online Payment Only</option>
                              <option value="both">Both (Online + Pay at Property)</option>
                            </select>
                            <p className="text-xs text-muted-foreground">
                              {formData.payment_mode === 'pay_at_property' && 'Guests can only pay at the property. Online payments are disabled.'}
                              {formData.payment_mode === 'online' && 'Guests must pay online via Apple Pay, Google Pay, or Credit Card.'}
                              {formData.payment_mode === 'both' && 'Guests can choose between online payment or paying at the property.'}
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Users tab (edit mode only) */}
                    {formTab === 'users' && selectedProperty && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5 text-indigo-500" />
                            <h3 className="font-medium text-gray-900">Assigned Users</h3>
                          </div>
                          <Button type="button" size="sm" onClick={handleSaveUsers} disabled={usersSaving} data-testid="save-users-btn">
                            {usersSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            Save Assignments
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">Select which users have access to this property.</p>
                        {usersLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {allUsers.filter(u => u.role !== 'superadmin').map(u => (
                              <label
                                key={u.id}
                                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${propertyUserIds.includes(u.id) ? 'bg-indigo-50 border-indigo-200' : 'hover:bg-accent/50'}`}
                                data-testid={`user-assign-${u.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={propertyUserIds.includes(u.id)}
                                  onChange={() => toggleUserAssignment(u.id)}
                                  className="rounded border-input"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm">{u.name}</p>
                                  <p className="text-xs text-muted-foreground">{u.email}</p>
                                </div>
                                <Badge variant="outline" className="text-[10px] flex-shrink-0">
                                  {u.role}
                                </Badge>
                              </label>
                            ))}
                            {allUsers.filter(u => u.role !== 'superadmin').length === 0 && (
                              <p className="text-muted-foreground text-center py-8 text-sm">No users available to assign.</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Footer - always visible */}
                <div className="flex justify-end gap-3 px-5 py-4 border-t bg-gray-50 flex-shrink-0">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="save-property-btn">
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {selectedProperty ? 'Update' : 'Create'} Property
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties table */}
        {properties.length === 0 ? (
          <div className="bg-white rounded-lg border border-border p-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No properties yet</h3>
            <p className="text-muted-foreground mb-4">
              Add your first property to get started with the booking engine.
            </p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Channex</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((property) => (
                  <TableRow key={property.id} data-testid={`property-row-${property.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                          {property.image_url ? (
                            <img
                              src={property.image_url}
                              alt={property.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Building2 className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{property.name}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {property.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {property.city}, {property.country}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{property.currency || 'USD'}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">/{property.slug}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            property.payment_mode === 'online' ? 'border-green-300 text-green-700' :
                            property.payment_mode === 'both' ? 'border-blue-300 text-blue-700' :
                            'border-amber-300 text-amber-700'
                          }`}
                        >
                          {property.payment_mode === 'online' ? 'Online' :
                           property.payment_mode === 'both' ? 'Online + Cash' : 'Cash Only'}
                        </Badge>
                        {property.mollie_configured && (
                          <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                            <CreditCard className="h-3 w-3" /> Mollie
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {property.channex_property_id ? (
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          Not configured
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          property.is_active
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                        }
                      >
                        {property.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`property-menu-${property.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(property)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {property.channex_property_id && (
                            <>
                              <DropdownMenuItem onClick={() => handleTestChannex(property)}>
                                <Wifi className="h-4 w-4 mr-2" />
                                Test Connection
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSyncChannex(property)}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Sync Rooms
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleRegisterWebhook(property)}
                                disabled={actionLoading[`webhook-${property.id}`]}
                              >
                                {actionLoading[`webhook-${property.id}`] ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <BellRing className="h-4 w-4 mr-2" />
                                )}
                                Register Webhook
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleSyncBookings(property)}
                                disabled={actionLoading[`sync-${property.id}`]}
                              >
                                {actionLoading[`sync-${property.id}`] ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4 mr-2" />
                                )}
                                Sync Bookings
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewWebhookLogs(property)}>
                                <ScrollText className="h-4 w-4 mr-2" />
                                Webhook Logs
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedProperty(property);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Delete confirmation dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Property</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedProperty?.name}"? This action cannot be
                undone and will also delete all associated rooms.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Webhook Logs dialog */}
        <Dialog open={webhookLogsOpen} onOpenChange={setWebhookLogsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                <ScrollText className="h-5 w-5" />
                Webhook Logs — {webhookLogsProperty?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              {webhookLogsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : webhookLogs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Bell className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No webhook events yet</p>
                  <p className="text-sm mt-1">
                    Events will appear here once Channex starts sending booking notifications.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {webhookLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border border-border rounded-lg p-3 text-sm"
                      data-testid={`webhook-log-${log.id}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={
                              log.event === 'booking_new' || log.event === 'booking'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : log.event === 'booking_modification'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : log.event === 'booking_cancellation'
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-gray-50 text-gray-700 border-gray-200'
                            }
                          >
                            {log.event}
                          </Badge>
                          {log.processed ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                              {log.result?.action || 'processed'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-[10px]">
                              pending
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {log.received_at ? new Date(log.received_at).toLocaleString() : '—'}
                        </span>
                      </div>
                      {log.payload && (
                        <div className="text-xs text-muted-foreground font-mono bg-muted/50 rounded p-2 mt-1">
                          {log.payload.booking_id && <span>Booking: {log.payload.booking_id.slice(0, 12)}...</span>}
                          {log.payload.revision_id && <span className="ml-3">Rev: {log.payload.revision_id.slice(0, 12)}...</span>}
                        </div>
                      )}
                      {log.result?.reason && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {log.result.reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
